// src/pages/VestingStrategic.js
import React, { useEffect, useState } from "react";
import { ethers } from "ethers";
import {
  PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis,
  LineChart, Line,
  CartesianGrid, Legend, ResponsiveContainer,
  ComposedChart
} from "recharts";
import "../styles/VestingStrategic.css";
import useWallet from "../hooks/useWallet";

const vestingAddress = "0x6fa2D0f273d86157FCE0C7631aac8A2d4f7C4A6a";

const strategicWallets = [
  {
    name: "Founder",
    address: "0x3A6163B029081e1C4b94CC98f2A6895CA314D76a",
    color: "#3b82f6"
  },
  {
    name: "Ecosystem",
    address: "0x73861188c241E9BCDC451E3121EaEa4aFB056cEC",
    color: "#10b981"
  },
  {
    name: "Marketing",
    address: "0x9BAE41FCc1e9c4E59B57419Ae4ff5ec9618774de",
    color: "#f59e0b"
  }
];

const abi = [
  "function getStrategicVestingData(address) view returns (uint256,uint256,uint256,uint256,uint256,uint256,uint256)",
  "function claimStrategicTokens(address strategicWallet)"
];

export default function StrategicVestingV2() {
  const { wallet, signer, connectWallet, wcProvider } = useWallet();
  const [vestingData, setVestingData] = useState({});
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile devices and tablets
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileOrTablet = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Tablet|Mobile/i.test(userAgent) ||
        (navigator.maxTouchPoints && navigator.maxTouchPoints > 2) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      setIsMobile(isMobileOrTablet);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get the correct provider based on connection type
  const getProvider = () => {
    if (wcProvider) {
      return new ethers.providers.Web3Provider(wcProvider);
    } else if (window.ethereum) {
      return new ethers.providers.Web3Provider(window.ethereum);
    } else {
      throw new Error("No provider available");
    }
  };

  // Handle wallet connection with mobile-optimized UI (numbered prompt)
  const handleConnectWallet = async () => {
    try {
      console.log("🔍 Connect wallet triggered");
      console.log("Is mobile/tablet:", isMobile);
      console.log("Has ethereum:", !!window.ethereum);
      
      if (isMobile && !window.ethereum) {
        console.log("📱 Mobile/Tablet detected without wallet, showing selection");
        
        const walletOptions = [
          "1️⃣ MetaMask",
          "2️⃣ Trust Wallet", 
          "3️⃣ Rainbow Wallet",
          "4️⃣ Coinbase Wallet",
          "5️⃣ WalletConnect (QR Code)"
        ].join('\n');
        
        const walletChoice = prompt(
          `Select your wallet:\n\n${walletOptions}\n\nEnter number (1-5):`
        );
        
        console.log("User selected:", walletChoice);
        
        switch(walletChoice?.trim()) {
          case '1':
            window.location.href = `https://metamask.app.link/dapp/${window.location.hostname}${window.location.pathname}`;
            break;
          case '2':
            window.location.href = `trust://open_url?coin_id=60&url=${window.location.href}`;
            break;
          case '3':
            window.location.href = `rainbow://open?url=${window.location.href}`;
            break;
          case '4':
            window.location.href = `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(window.location.href)}`;
            break;
          case '5':
          default:
            console.log("🔄 Initiating WalletConnect");
            const existingModals = document.querySelectorAll('wcm-modal, w3m-modal');
            existingModals.forEach(modal => modal.remove());
            
            await connectWallet();
            
            setTimeout(() => {
              const modals = document.querySelectorAll('wcm-modal, w3m-modal');
              modals.forEach(modal => {
                modal.style.display = 'none';
                modal.remove();
              });
            }, 2000);
            break;
        }
      } else {
        console.log("💻 Standard connection flow");
        await connectWallet();
        
        setTimeout(() => {
          const modals = document.querySelectorAll('wcm-modal, w3m-modal');
          modals.forEach(modal => {
            modal.style.display = 'none';
          });
        }, 2000);
      }
    } catch (error) {
      console.error("Connection error:", error);
      alert("Failed to connect wallet. Please try again.");
    }
  };

  useEffect(() => {
    if (wallet && signer) {
      const _contract = new ethers.Contract(vestingAddress, abi, signer);
      loadAllVestingData(_contract);
    }
  }, [wallet, signer]);

  const loadAllVestingData = async (_contract) => {
    try {
      setLoading(true);
      const data = {};
      for (const item of strategicWallets) {
        const result = await _contract.getStrategicVestingData(item.address);
        data[item.address] = {
          claimed: Number(result[0]) / 1e18,
          claimable: Number(result[1]) / 1e18,
          nextUnlock: Number(result[2]),
          total: Number(result[3]) / 1e18,
          tgeAmount: Number(result[4]) / 1e18,
          unlocked: Number(result[5]) / 1e18,
          monthsLeft: Number(result[6])
        };
      }
      setVestingData(data);
    } catch (err) {
      console.error("Loading vesting data failed", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async (addr) => {
    if (!wallet) {
      alert("❗ Please connect your wallet.");
      return;
    }

    try {
      setLoading(true);
      const _provider = getProvider();
      const _signer = _provider.getSigner();
      const _contract = new ethers.Contract(vestingAddress, abi, _signer);

      // Network check with retry for mobile
      let network;
      let retries = 3;
      while (retries > 0) {
        try {
          network = await _provider.getNetwork();
          break;
        } catch (error) {
          retries--;
          if (retries === 0) throw error;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      if (network.chainId !== 1) {
        alert("⚠️ Please switch your wallet to Ethereum Mainnet.");
        return;
      }

      const tx = await _contract.claimStrategicTokens(addr);
      await tx.wait();
      alert("✅ Tokens claimed!");
      loadAllVestingData(_contract);
      
      // Clean up modals for mobile
      setTimeout(() => {
        const modals = document.querySelectorAll('wcm-modal, w3m-modal');
        modals.forEach(modal => {
          modal.style.display = 'none';
        });
      }, 1000);
    } catch (err) {
      console.error("Claim failed:", err);
      alert("❌ Claim failed. " + (err.reason || err.message));
    } finally {
      setLoading(false);
    }
  };

  const pieData = strategicWallets.map(item => ({
    name: item.name,
    value: vestingData[item.address]?.total || 0
  }));

  const tgeData = strategicWallets.map(item => ({
    name: item.name,
    tge: vestingData[item.address]?.tgeAmount || 0,
    color: item.color
  }));

  const unlockedData = strategicWallets.map(item => ({
    name: item.name,
    unlocked: vestingData[item.address]?.unlocked || 0
  }));

  const stackedData = strategicWallets.map(item => ({
    name: item.name,
    unlocked: vestingData[item.address]?.unlocked || 0,
    locked: (vestingData[item.address]?.total || 0) - (vestingData[item.address]?.unlocked || 0)
  }));

  const isStrategicWallet = strategicWallets.some(sw => wallet?.toLowerCase() === sw.address.toLowerCase());

  return (
    <div className="vesting-container">
      <h1>🌟 Governed Token Vesting for Growth & Stability</h1>

      <p className="strategic-vesting-intro">
        MediCoin implements a strategic, time-locked vesting model across key allocations — including Marketing, Ecosystem Development, Founders, Team & Advisors — to ensure responsible token release and long-term alignment.
      </p>

      {!wallet ? (
        <button className="connect-btn" onClick={handleConnectWallet}>Connect Wallet</button>
      ) : null}

      {loading ? <p>Loading data...</p> : (
        <>
          <div className="vesting-charts-grid">
            <div className="chart-item">
              <h3>Total Allocations</h3>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie dataKey="value" data={pieData} cx="50%" cy="50%" outerRadius={100} label>
                    {strategicWallets.map((item, index) => (
                      <Cell key={`cell-${index}`} fill={item.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-item">
              <h3>TGE Unlock Amounts</h3>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tgeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="tge">
                    {tgeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-item">
              <h3>Unlocked Progress</h3>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={unlockedData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="unlocked" stroke="#22d3ee" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-item">
              <h3>Unlocked vs Locked</h3>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={stackedData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="unlocked" stackId="a" fill="#22c55e" />
                  <Bar dataKey="locked" stackId="a" fill="#ef4444" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <h3>🔥 Strategic Vesting Details</h3>
          <table className="vesting-table">
            <thead>
              <tr>
                <th>Wallet Name</th>
                <th>Total Allocated</th>
                <th>TGE Unlocked</th>
                <th>Unlocked So Far</th>
                <th>Claimable</th>
                <th>Months Left</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {strategicWallets.map((item, index) => (
                <tr key={index}>
                  <td>{item.name}</td>
                  <td>{(vestingData[item.address]?.total || 0).toLocaleString()}</td>
                  <td>{(vestingData[item.address]?.tgeAmount || 0).toLocaleString()}</td>
                  <td>{(vestingData[item.address]?.unlocked || 0).toLocaleString()}</td>
                  <td>{(vestingData[item.address]?.claimable || 0).toLocaleString()}</td>
                  <td>{vestingData[item.address]?.monthsLeft || 0}</td>
                  <td>
                    {(wallet?.toLowerCase() === item.address.toLowerCase()) ? (
                      <button className="claim-btn" onClick={() => handleClaim(item.address)}>Claim</button>
                    ) : (
                      isStrategicWallet ? <button className="claim-btn" disabled>Connect Correct Wallet</button> : null
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
